import React, { useEffect, useRef, useState } from "react";
import styles from "./integrations.module.scss";
import base from "../../styles/company.module.scss";
import { Toggle } from "apps/company/components/toggle";
import { Button } from "components/ui";
import { LongCard } from "apps/company/components/card";
import { FetchResult, useMutation, useQuery } from "@apollo/client";
import { QUERY_COMPANY_INTEGRATIONS_SCREEN } from "apps/company/api/graphQL/query";
import appInfo from "utility/app-info";
import { CompanyAdmin } from "apps/company/common/dataTypes";
import {
  CompanyPortalIntegrationsScreen,
  CompanySSO,
  Domains,
  HRIS,
  SIGN_IN_TOGGLE,
  SSOItem,
} from "./dataTypes";
import { getToggleIcon } from "./utils";
import {
  AddDomainDialog,
  OktaDialog,
  StatusDialog,
  TemplateDialog,
} from "apps/company/components/common/modal";
import {
  MUTATION_REMOVE_COMPANY_DOMAIN,
  MUTATION_REMOVE_HRIS,
  MUTATION_SELF_SIGN_UP_UPDATE,
  MUTATION_SSO_UPDATE,
} from "apps/company/api/graphQL/mutation";
import _ from "lodash";
import { CommonModalProps } from "apps/company/components/common/modal/common";
import { BUTTON_ACTION } from "apps/company/common/constants";
import { PageLoading } from "apps/company/components/loading";
import PortalErrorPage from "apps/company/components/error";
import { useLocation } from "react-router-dom";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import CloseIcon from "Assets/icon/icon_close.svg";
import { logError } from "utility/Function";
import { Trans } from "react-i18next";
import { useTranslation } from "lib/i18n/use-translation";

const SSO_SETUP_GUIDE_URL =
  "https://walkingonearth.notion.site/How-do-I-enable-single-sign-on-SSO-e886a0f72b9a4f07a39f449f01b6b344";

const Integrations = () => {
  //HOOKS
  const { t } = useTranslation(["admin", "common"]);
  const { data, loading, refetch, error } = useQuery(
    QUERY_COMPANY_INTEGRATIONS_SCREEN,
    {
      variables: {
        appInfo,
      },
    }
  );
  const [removeDomain] = useMutation(MUTATION_REMOVE_COMPANY_DOMAIN);
  const [updateSSOptions] = useMutation(MUTATION_SSO_UPDATE);
  const [updateSelfSignUp] = useMutation(MUTATION_SELF_SIGN_UP_UPDATE);
  const [removeHRIS] = useMutation(MUTATION_REMOVE_HRIS);

  const [integrations, setIntegrations] =
    useState<CompanyPortalIntegrationsScreen>();
  const [title, setTitle] = useState<string>(t("admin:integrations.title"));
  const [subTitle, setSubtitle] = useState<string>("");
  const [domains, setDomains] = useState<Domains>();
  const [sso, setSSO] = useState<CompanySSO>();
  const [hris, setHRIS] = useState<HRIS>();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const hris_error = searchParams.get("hris_error");
  const [showErrorModal, setShowErrorModal] = useState<boolean>(false);
  const handleCloseConnectionErrors = async () => {
    setShowErrorModal(false);
  };

  useEffect(() => {
    if (hris_error) {
      setShowErrorModal(true);
    }
  }, [hris_error]);

  useEffect(() => {
    if (data) {
      const { title, subTitle, domains, sso, hris } = (data as CompanyAdmin)
        .companyPortalIntegrationsScreen;
      setIntegrations(integrations);
      setTitle(title);
      setDomains(domains);
      setSubtitle(subTitle);
      setDomains(domains);
      setSSO(sso);
      setHRIS(hris);
    }
  }, [hris_error, data, loading, integrations]);

  const [selectedDomain, setSelectedDomain] = useState<string>();
  const [selectedHRIS, setSelectedHRIS] = useState<string>();
  const [activeAction, setActiveAction] = useState<
    BUTTON_ACTION | SIGN_IN_TOGGLE | string
  >();
  const toggleItem = useRef<SSOItem>(null);

  // MODAL STATES
  const [modalInfo, setModalInfo] =
    useState<Pick<CommonModalProps, "title" | "description" | "action">>();
  const [isTemplateModalOpen, setIsTemplateModalOpen] =
    useState<boolean>(false);
  const [isStatusModalOpen, setIsStatusModalOpen] = useState<boolean>(false);
  const [isAddDomainModalOpen, setIsAddDomainModalOpen] =
    useState<boolean>(false);
  const [isOktaModalOpen, setIsOktaModalOpen] = useState<boolean>(false);

  // HANDLERS
  if (loading) {
    return (
      <div className={styles["integrations-container"]}>
        <PageLoading />
      </div>
    );
  }
  if (error) {
    return <PortalErrorPage pageTitle={t("admin:nav.integrations")} />;
  }
  const handleAddDomain = () => {
    setModalInfo({
      title: t("admin:integrations.addDomain.title"),
      description: t("admin:integrations.addDomain.description"),
      action: t("admin:action.add"),
    });
    setActiveAction(BUTTON_ACTION.ADD_DOMAIN);
    setIsAddDomainModalOpen(true);
  };

  const handleRemoveDomain = (domain: string) => {
    setSelectedDomain(domain);
    setActiveAction(BUTTON_ACTION.REMOVE_DOMAIN);
    setModalInfo({
      title: t("admin:integrations.removeDomain.title"),
      description: t("admin:integrations.removeDomain.description"),
      action: t("admin:action.remove"),
    });

    setIsTemplateModalOpen(true);
  };

  const updateToggleItem = async (
    toggleItem: React.MutableRefObject<SSOItem>
  ) => {
    return updateSSOptions({
      variables: {
        appInfo,
        input: {
          authId: toggleItem.current.id,
          isSignInEnabled: !toggleItem.current.isEnabled,
        },
      },
    })
      .then((response) => {
        if (response.errors) {
          setModalInfo({
            title: t("admin:error.defaultTitle"),
            description: response.errors[0].message,
            action: t("admin:action.close"),
          });
          setIsTemplateModalOpen(true);
        } else if (response.data) {
          refetch();
        }
      })
      .catch((error) => {
        logError("Error updating sign in option", error);
        setModalInfo({
          title: t("admin:error.defaultTitle"),
          description: error.message,
          action: t("admin:action.close"),
        });
        setIsTemplateModalOpen(true);
      });
  };

  const handleDialogAction = async () => {
    //Handle all actions after template dialog action button is clicked.
    switch (activeAction) {
      case BUTTON_ACTION.REMOVE_DOMAIN:
        setActiveAction(null);
        if (selectedDomain) {
          await removeDomain({
            variables: {
              appInfo,
              domain: selectedDomain,
            },
          })
            .then((response) => {
              if (response.errors) {
                setModalInfo({
                  title: t("admin:error.defaultTitle"),
                  description: response.errors[0].message,
                  action: t("admin:action.close"),
                });
                setIsTemplateModalOpen(true);
              } else if (response.data) {
                refetch();
                setModalInfo({
                  title: t("admin:integrations.domainRemoved"),
                  action: t("admin:action.close"),
                });
                setIsStatusModalOpen(true);
              }
            })
            .catch((error) => {
              logError("Error removing domain", error);
              setModalInfo({
                title: t("admin:error.defaultTitle"),
                description: error.message,
                action: t("admin:action.close"),
              });
              setIsTemplateModalOpen(true);
            });
          setSelectedDomain("");
        }
        break;
      case BUTTON_ACTION.ADD_DOMAIN:
        refetch();
        break;
      case SIGN_IN_TOGGLE.SELF_SIGNUP:
        setActiveAction(null);
        if (sso?.signUp) {
          await updateSelfSignUp({
            variables: {
              appInfo,
              isSignUpEnabled: !sso?.signUp?.isEnabled,
            },
          })
            .then((response) => {
              if (response.errors) {
                setModalInfo({
                  title: t("admin:error.defaultTitle"),
                  description: response.errors[0].message,
                  action: t("admin:action.close"),
                });
                setIsTemplateModalOpen(true);
              } else if (response.data) {
                refetch();
              }
            })
            .catch((error) => {
              logError("Error updating sign in option", error);
              setModalInfo({
                title: t("admin:error.defaultTitle"),
                description: error.message,
                action: t("admin:action.close"),
              });
              setIsTemplateModalOpen(true);
            });
        }
        break;
      case SIGN_IN_TOGGLE.GOOGLE:
        setActiveAction(null);
        await updateToggleItem(toggleItem);
        toggleItem.current = null;
        break;
      case SIGN_IN_TOGGLE.MAGIC_LINK:
        setActiveAction(null);
        await updateToggleItem(toggleItem);
        toggleItem.current = null;
        break;
      case SIGN_IN_TOGGLE.MICROSOFT:
        setActiveAction(null);
        await updateToggleItem(toggleItem);
        toggleItem.current = null;
        break;
      case SIGN_IN_TOGGLE.OKTA:
        setActiveAction(null);
        await updateToggleItem(toggleItem);
        toggleItem.current = null;
        break;
      case "hris":
        setActiveAction(null);
        removeHRIS({
          variables: {
            appInfo,
            hrisConnectionId: selectedHRIS,
          },
        })
          .then((response) => {
            if (response.errors) {
              setModalInfo({
                title: t("admin:error.defaultTitle"),
                description: response.errors[0].message,
                action: t("admin:action.close"),
              });
              setIsTemplateModalOpen(true);
            } else if (response.data) {
              refetch();
            }
          })
          .catch((error) => {
            logError("Error removing hris", error);
            setModalInfo({
              title: t("admin:error.defaultTitle"),
              description: error.message,
              action: t("admin:action.close"),
            });
            setIsTemplateModalOpen(true);
          });
        break;
      default:
        break;
    }
  };

  const handleToggleSignUp = () => {
    setActiveAction(SIGN_IN_TOGGLE.SELF_SIGNUP);
    if (!sso?.signUp?.isEnabled) {
      setModalInfo({
        title: t("admin:integrations.justChecking"),
        description: (
          <Trans
            i18nKey="admin:integrations.selfSignUpConfirm"
            components={[
              // Trans swaps these children for the catalog's link text; the
              // placeholder is what jsx-a11y/anchor-has-content reads
              // statically, and an expression keeps the i18n guard happy.
              <a href={SSO_SETUP_GUIDE_URL} target="_blank" rel="noreferrer">
                {"link"}
              </a>,
            ]}
          />
        ),
        action: t("admin:action.yes"),
      });
      setIsTemplateModalOpen(true);
    } else {
      updateSelfSignUp({
        variables: {
          appInfo,
          isSignUpEnabled: !sso?.signUp?.isEnabled,
        },
      })
        .then((response) => {
          if (response.errors) {
            setModalInfo({
              title: t("admin:error.defaultTitle"),
              description: response.errors[0].message,
              action: t("admin:action.close"),
            });
            setIsTemplateModalOpen(true);
          } else if (response.data) {
            refetch();
          }
        })
        .catch((error) => {
          logError("Error updating self sign up", error);
          setModalInfo({
            title: t("admin:error.defaultTitle"),
            description: error.message,
            action: t("admin:action.close"),
          });
          setIsTemplateModalOpen(true);
        });
    }
  };

  const handleConnectHRIS = () => {
    window.open(hris?.link, "_self");
  };
  const handleRemoveHRIS = (hrisConnectionId: string) => {
    setSelectedHRIS(hrisConnectionId);
    setActiveAction("hris");
    setModalInfo({
      title: t("admin:integrations.removeHris.title"),
      description: t("admin:integrations.removeHris.description"),
      action: t("admin:action.remove"),
    });
    setIsTemplateModalOpen(true);
  };

  const handleToggle = (item: SSOItem) => {
    toggleItem.current = item;
    setActiveAction(item.id as SIGN_IN_TOGGLE);

    if (item.id === SIGN_IN_TOGGLE.OKTA) {
      if (item.isEnabled) {
        setModalInfo({
          title: t("admin:integrations.removeOkta.title"),
          description: t("admin:integrations.removeOkta.description"),
          action: t("admin:action.remove"),
        });
        setIsTemplateModalOpen(true);
      } else {
        setIsOktaModalOpen(true);
      }
    } else if (item.id === SIGN_IN_TOGGLE.PASSWORD) {
      updateSSOptions({
        variables: {
          appInfo,
          input: {
            authId: item.id,
            isSignInEnabled: !item.isEnabled,
          },
        },
      })
        .then((response) => {
          if (response.errors) {
            setModalInfo({
              title: t("admin:error.defaultTitle"),
              description: response.errors[0].message,
              action: t("admin:action.close"),
            });

            setIsTemplateModalOpen(true);
          } else if (response.data) {
            refetch();
          }
        })
        .catch((error) => {
          logError("Error updating self sign up", error);
          setModalInfo({
            title: t("admin:error.defaultTitle"),
            description: error.message,
            action: t("admin:action.close"),
          });
          setIsTemplateModalOpen(true);
        });
    } else {
      //If item is not active, show confirmation modal else proceed to deactivate the item
      if (!item.isEnabled) {
        setModalInfo({
          title: t("admin:integrations.justChecking"),
          description: (
            <Trans
              i18nKey="admin:integrations.ssoGuideConfirm"
              components={[
                <a href={SSO_SETUP_GUIDE_URL} target="_blank" rel="noreferrer">
                  {"link"}
                </a>,
              ]}
            />
          ),
          action: t("admin:action.yes"),
        });
        setIsTemplateModalOpen(true);
      } else {
        setActiveAction(null);
        updateSSOptions({
          variables: {
            appInfo,
            input: {
              authId: item.id,
              isSignInEnabled: !item.isEnabled,
            },
          },
        })
          .then((response) => {
            if (response.errors) {
              setModalInfo({
                title: t("admin:error.defaultTitle"),
                description: response.errors[0].message,
                action: t("admin:action.close"),
              });
              setIsTemplateModalOpen(true);
            } else if (response.data) {
              refetch();
            }
          })
          .catch((error) => {
            logError("Error updating self sign up", error);
            setModalInfo({
              title: t("admin:error.defaultTitle"),
              description: error.message,
              action: t("admin:action.close"),
            });
            setIsTemplateModalOpen(true);
          });
      }
    }
  };

  const handleOktaResponse = (response: FetchResult) => {
    setActiveAction(null);
    if (response.data) {
      refetch();
      setModalInfo({
        title: t("admin:integrations.okta.submitted.title"),
        description: (
          <Trans i18nKey="admin:integrations.okta.submitted.description" />
        ),
        action: t("admin:action.close"),
      });
      setIsTemplateModalOpen(true);
    } else if (response.errors) {
      setModalInfo({
        title: t("admin:error.defaultTitle"),
        description: response.errors[0].message,
        action: t("admin:action.close"),
      });
      setIsTemplateModalOpen(true);
    }
  };

  return (
    <div className={styles["integrations-container"]}>
      {loading ? (
        <PageLoading />
      ) : (
        <div className={base["content"]}>
          <AddDomainDialog
            isOpen={isAddDomainModalOpen}
            title={modalInfo?.title}
            description={modalInfo?.description}
            action={modalInfo?.action}
            onCloseAction={() => setIsAddDomainModalOpen(false)}
            onAction={handleDialogAction}
          />
          <TemplateDialog
            isOpen={isTemplateModalOpen}
            title={modalInfo?.title}
            description={modalInfo?.description}
            action={modalInfo?.action}
            onCloseAction={() => setIsTemplateModalOpen(false)}
            onAction={handleDialogAction}
          />
          <OktaDialog
            isOpen={isOktaModalOpen}
            data={toggleItem.current}
            onAPIResponse={handleOktaResponse}
            onCloseAction={() => setIsOktaModalOpen(false)}
          />
          <StatusDialog
            isOpen={isStatusModalOpen}
            title={modalInfo?.title}
            action={modalInfo?.action}
            onCloseAction={() => setIsStatusModalOpen(false)}
          />
          <h1 className={base["main-title"]}>{title}</h1>
          <p className={base["main-subtitle"]}>{subTitle}</p>
          <div className={styles["integrations-content"]}>
            <div className={styles["integrations-title"]}>
              <div className={styles["text"]}>
                <h2>{domains?.title}</h2>
                <p className={styles["text-small"]}>{domains?.subTitle}</p>
              </div>
              <Button className="sans" onClick={handleAddDomain}>
                {domains?.addButton.buttonText}
              </Button>
            </div>
            <table>
              <thead>
                <tr>
                  {domains?.labels?.map((header) => (
                    <th key={header}>{header}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {domains?.domainList?.map((domain) => (
                  <tr key={domain.name}>
                    <td>{domain.name}</td>
                    <td>{domain.dateAdded}</td>
                    <td onClick={() => handleRemoveDomain(domain.name)}>
                      {domain.action.buttonText}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {_.isEmpty(domains?.domainList) && (
              <div className={styles["common-no-results"]}>
                <h3>{t("admin:integrations.noDomains")}</h3>
              </div>
            )}

            <h2>
              {sso?.title}
              {/* <img
                src={InfoIcon}
                alt="info icon"
                onClick={() =>
                  handleInfo({
                    title: "SSO Integration",
                    description: (
                      <>
                        We&apos;ve made it easy for your existing company logins
                        to work with WONE.
                        <br />
                        <br /> Simply toggle the options to suit your setup. You
                        can find more information on setup <a href="">here.</a>
                      </>
                    ),
                  })
                }
              /> */}
            </h2>
            <p
              className={styles["text-small"]}
              dangerouslySetInnerHTML={{ __html: sso?.subTitle }}
            ></p>
            <div className={styles["sso-options"]}>
              <h3>{sso?.signIn?.title}</h3>
              <div className={styles["sign-on"]}>
                {sso?.signIn?.items.map((item) => (
                  <Toggle
                    key={item.id}
                    icon={getToggleIcon(item.id)}
                    styling={styles["switch"]}
                    label={item.name}
                    checked={item.isEnabled}
                    disabled={_.isEmpty(domains?.domainList)}
                    onChange={() => handleToggle(item)}
                  />
                ))}
              </div>
            </div>

            <div className={styles["signup"]}>
              <h3>{sso?.signUp?.title} </h3>
              <Toggle
                label={t("admin:integrations.allowSelfSignUp")}
                styling={styles["switch"]}
                checked={sso?.signUp?.isEnabled}
                disabled={_.isEmpty(domains?.domainList)}
                onChange={handleToggleSignUp}
              />
            </div>
            <div className={styles["hris"]}>
              <h2>{hris?.title} </h2>
              <p
                className={styles["text-small"]}
                dangerouslySetInnerHTML={{ __html: hris?.subTitle }}
              ></p>
              <div className={styles["hris-connection"]}>
                {hris?.connections.map((connection) => (
                  <LongCard
                    key={connection.id}
                    id={connection.id}
                    label={connection.name}
                    icon={connection.logo}
                    subCompanyName={connection.subCompanyName}
                    onDelete={() => handleRemoveHRIS(connection.id)}
                  />
                ))}
                <Button onClick={handleConnectHRIS}>
                  {t("admin:integrations.connectHris")}
                </Button>
              </div>
            </div>
          </div>
          <Modal
            isOpen={showErrorModal}
            centered
            className={styles["connection-error-modal"]}
          >
            <ModalHeader className={styles["connection-error-modal-header"]}>
              <img
                src={CloseIcon}
                alt="Close Connection"
                onClick={handleCloseConnectionErrors}
              />
            </ModalHeader>
            <ModalBody className={styles["connection-error-modal-body"]}>
              <p className={styles["main-message"]}>
                {t("admin:integrations.hrisError.title")}
              </p>
              <p className={styles["error-message"]}>
                {hris_error}
                <p className={`text-small ${styles["sub-error-message"]}`}>
                  <Trans
                    i18nKey="admin:integrations.hrisError.contact"
                    components={[
                      <a
                        rel="noreferrer"
                        href="mailto:support@walkingonearth.com"
                        className="text-primary"
                      >
                        {"link"}
                      </a>,
                    ]}
                  />
                </p>
              </p>
            </ModalBody>
            <ModalFooter className={styles["connection-error-modal-footer"]}>
              <Button onClick={handleConnectHRIS}>
                {t("common:action.tryAgain")}
              </Button>
            </ModalFooter>
          </Modal>
        </div>
      )}
    </div>
  );
};

export default Integrations;
